/**
 * Host Nonsole Participation — shared contract
 *
 * Classifies each reachable machine into exactly one participation bucket using
 * a fixed severity precedence. Consumed by realtime-api aggregation, daily
 * snapshots, and the admin-dashboard Host Nonsole Participation page.
 */

import { z } from 'zod';

// ============================================================================
// Buckets
// ============================================================================

// Precedence order (also drives display order). Real readiness gates rank
// ABOVE owner/attestation: owner attribution no longer gates nonsole entry (see
// node-service `updateUnattendedConfiguration`), so a host blocked for a real
// reason (version, drivers, power, idle, ...) must be bucketed by that reason
// rather than misreported as "needs sign-in". NO_OWNER / NOT_ATTESTED are now
// informational (attribution only) and only surface once nothing real blocks
// the host.
export const participationBucketValues = [
  'IN_HOST_MODE_HOSTING',
  'IN_HOST_MODE_IDLE',
  'POWERED_OFF',
  'SLEEPING',
  'POWER_UNKNOWN',
  'BELOW_MIN_VERSION',
  'DRIVERS_BROKEN',
  'AC_POWER',
  'IDLE_TIMER',
  'ROLLOUT_ENABLED',
  'SESSION_COUNT',
  'PRESENCE_HOLD',
  'OTHER_BLOCKED',
  'NO_OWNER',
  'NOT_ATTESTED',
  'NO_READINESS_DATA',
  'READY_NOT_HOSTING',
] as const;

export type ParticipationBucket = (typeof participationBucketValues)[number];

export const participationBucketSchema = z.enum(participationBucketValues);

/**
 * Gate IDs that classify as "drivers broken" (override AC_POWER / IDLE_TIMER).
 * Only true driver-health gates (category 'health') belong here. Environment
 * gates like VIRTUAL_CONTROLLER (XInput slot availability) are NOT driver
 * health and fall through to OTHER_BLOCKED.
 */
export const DRIVER_GATE_IDS = [
  'INPUT_DRIVERS_HEALTHY',
  'PLAYCAST_DRIVERS_HEALTHY',
] as const;

export const PARTICIPATION_BUCKET_LABELS: Record<ParticipationBucket, string> = {
  IN_HOST_MODE_HOSTING: 'In Host Mode (hosting)',
  IN_HOST_MODE_IDLE: 'In Host Mode (idle)',
  POWERED_OFF: 'Powered off',
  SLEEPING: 'Sleeping',
  POWER_UNKNOWN: 'Unknown power status',
  NO_OWNER: 'No owner',
  NOT_ATTESTED: 'Owner claimed — no attestation',
  BELOW_MIN_VERSION: 'Below minimum version',
  DRIVERS_BROKEN: 'Drivers broken',
  AC_POWER: 'Not on AC power',
  IDLE_TIMER: 'User not idle long enough',
  ROLLOUT_ENABLED: 'Nonsole rollout not enabled',
  SESSION_COUNT: 'No spare Windows session slot',
  PRESENCE_HOLD: 'App/driver keeping host awake',
  OTHER_BLOCKED: 'Blocked (other gate)',
  NO_READINESS_DATA: 'No readiness data',
  READY_NOT_HOSTING: 'Ready — not in Host Mode',
};

/**
 * Display order: success buckets first, then non-participation by precedence.
 * `participationBucketValues` is already declared in that order, so reuse it as
 * the single source of truth rather than duplicating the list.
 */
export const PARTICIPATION_BUCKET_DISPLAY_ORDER: ParticipationBucket[] = [
  ...participationBucketValues,
];

/** Non-participation buckets only (excludes in-host-mode success buckets). */
export const NON_PARTICIPATION_BUCKETS: ParticipationBucket[] =
  PARTICIPATION_BUCKET_DISPLAY_ORDER.filter(
    (b) => b !== 'IN_HOST_MODE_HOSTING' && b !== 'IN_HOST_MODE_IDLE',
  );

export const DEFAULT_REACHABILITY_DAYS = 7;
export const MAX_REACHABILITY_DAYS = 90;

/** Node-service heartbeat interval — mirrors apps/node-service/src/realtime/index.ts. */
export const REACHABILITY_HEARTBEAT_MS = 5 * 60 * 1000;
/** Grace period on top of the heartbeat interval to account for network jitter. */
export const REACHABILITY_GRACE_MS = 60 * 1000;
/** Total window: a machine is "connected now" when nodeServiceLastSeenAt is within this. */
export const REACHABILITY_WINDOW_MS =
  REACHABILITY_HEARTBEAT_MS + REACHABILITY_GRACE_MS;

// ============================================================================
// Classifier
// ============================================================================

/** Query-time power display state (matches FleetService.derivePowerDisplay output). */
export type PowerDisplayState = 'awake' | 'asleep' | 'off' | 'unknown';

export interface ParticipationMachineSignals {
  isCloudHost: boolean;
  hasOwner: boolean;
  isAttested: boolean;
  belowMinVersion: boolean;
  blockingGateIds: string[];
  readinessFresh: boolean;
  inHostMode: boolean;
  isHosting: boolean;
  /** Derived from machine power fields + staleness. Omitted/awake → fall through to readiness gates. */
  powerDisplayState?: PowerDisplayState;
}

export function classifyParticipation(signals: ParticipationMachineSignals): ParticipationBucket {
  if (signals.inHostMode) {
    return signals.isHosting ? 'IN_HOST_MODE_HOSTING' : 'IN_HOST_MODE_IDLE';
  }

  // Power state dominates readiness gates: a machine that is off, asleep, or has
  // lost contact (scoped unknown from stale awake / stored unknown) is diagnosed
  // by power before version/drivers/idle gates.
  if (signals.powerDisplayState === 'off') {
    return 'POWERED_OFF';
  }
  if (signals.powerDisplayState === 'asleep') {
    return 'SLEEPING';
  }
  if (signals.powerDisplayState === 'unknown') {
    return 'POWER_UNKNOWN';
  }

  // Real readiness gates first. Owner attribution no longer blocks nonsole
  // entry, so a host held back by version / drivers / power / idle / other
  // gates is diagnosed by that gate rather than by its (now non-blocking)
  // owner or attestation state.
  if (signals.belowMinVersion) {
    return 'BELOW_MIN_VERSION';
  }
  if (DRIVER_GATE_IDS.some((id) => signals.blockingGateIds.includes(id))) {
    return 'DRIVERS_BROKEN';
  }
  if (signals.blockingGateIds.includes('AC_POWER')) {
    return 'AC_POWER';
  }
  if (signals.blockingGateIds.includes('IDLE_TIMER')) {
    return 'IDLE_TIMER';
  }
  // The three highest-volume gates that were previously lumped into
  // OTHER_BLOCKED, broken out into their own buckets. Ordered among themselves by
  // the host's registry (gate-evaluation) order: ROLLOUT_ENABLED (prerequisite)
  // then SESSION_COUNT (prerequisite) then PRESENCE_HOLD (entry condition). They
  // sit below AC_POWER/IDLE_TIMER so those existing buckets are unchanged — only
  // the OTHER_BLOCKED catch-all is subdivided.
  //   ROLLOUT_ENABLED : nonsole rollout is not enabled for this machine.
  //   SESSION_COUNT   : no spare Windows session slot (someone is signed in).
  //   PRESENCE_HOLD   : an app/driver power-request hold or live fullscreen
  //                     window is keeping the host awake (distinct from
  //                     IDLE_TIMER's user-input recency — the human may be away).
  if (signals.blockingGateIds.includes('ROLLOUT_ENABLED')) {
    return 'ROLLOUT_ENABLED';
  }
  if (signals.blockingGateIds.includes('SESSION_COUNT')) {
    return 'SESSION_COUNT';
  }
  if (signals.blockingGateIds.includes('PRESENCE_HOLD')) {
    return 'PRESENCE_HOLD';
  }
  if (signals.blockingGateIds.length > 0) {
    return 'OTHER_BLOCKED';
  }

  // Informational (attribution only): surfaced once nothing real blocks the
  // host. These do NOT prevent the host from entering nonsole mode.
  if (!signals.isCloudHost && !signals.hasOwner) {
    return 'NO_OWNER';
  }
  if (!signals.isCloudHost && signals.hasOwner && !signals.isAttested) {
    return 'NOT_ATTESTED';
  }

  if (!signals.readinessFresh) {
    return 'NO_READINESS_DATA';
  }
  return 'READY_NOT_HOSTING';
}

export function isReadinessFresh(
  evaluatedAtUtc: string | null | undefined,
  staleMinutes: number,
  nowMs: number = Date.now(),
): boolean {
  if (!evaluatedAtUtc || evaluatedAtUtc.trim().length === 0) {
    return false;
  }
  const evaluatedMs = Date.parse(evaluatedAtUtc);
  if (!Number.isFinite(evaluatedMs)) {
    return false;
  }
  // A timestamp ahead of now (clock skew or bogus data) is not trustworthy —
  // treat it as not fresh rather than letting the negative age pass the window.
  if (evaluatedMs > nowMs) {
    return false;
  }
  return nowMs - evaluatedMs <= staleMinutes * 60 * 1000;
}

export function clampReachabilityDays(input: number | undefined): number {
  if (typeof input !== 'number' || !Number.isFinite(input)) {
    return DEFAULT_REACHABILITY_DAYS;
  }
  if (input < 1) return 1;
  if (input > MAX_REACHABILITY_DAYS) return MAX_REACHABILITY_DAYS;
  return Math.floor(input);
}

export function parseReachabilityDaysParam(raw: unknown): number | undefined {
  if (raw === undefined || raw === null || raw === '') return undefined;
  if (typeof raw !== 'string') return undefined;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || !Number.isInteger(parsed) || parsed < 1 || parsed > MAX_REACHABILITY_DAYS) {
    throw new Error(`reachabilityDays must be a positive integer <= ${MAX_REACHABILITY_DAYS}`);
  }
  return parsed;
}

// ============================================================================
// API response shapes
// ============================================================================

export const participationReasonRowSchema = z.object({
  bucket: participationBucketSchema,
  label: z.string(),
  count: z.number().int().nonnegative(),
});
export type ParticipationReasonRow = z.infer<typeof participationReasonRowSchema>;

export const hostNonsoleParticipationTotalsSchema = z.object({
  allActive: z.number().int().nonnegative(),
  connectedNow: z.number().int().nonnegative(),
  disconnected: z.number().int().nonnegative(),
  uninstalled: z.number().int().nonnegative(),
  /** @deprecated Legacy alias for connectedNow — kept for one release so old snapshots parse. */
  reachable: z.number().int().nonnegative().optional(),
  /** @deprecated Legacy alias for disconnected — kept for one release so old snapshots parse. */
  dormant: z.number().int().nonnegative().optional(),
});
export type HostNonsoleParticipationTotals = z.infer<typeof hostNonsoleParticipationTotalsSchema>;

export const hostNonsoleParticipationInHostModeSchema = z.object({
  hosting: z.number().int().nonnegative(),
  idle: z.number().int().nonnegative(),
});
export type HostNonsoleParticipationInHostMode = z.infer<typeof hostNonsoleParticipationInHostModeSchema>;

export const hostNonsoleParticipationSummarySchema = z.object({
  generatedAt: z.number(),
  /** @deprecated No-op since the reachable window is now fixed at 5 min + 1 min grace. Echoed for backward compat; will be removed in a future release. */
  reachabilityDays: z.number().int().positive(),
  staleMinutes: z.number().int().positive(),
  totals: hostNonsoleParticipationTotalsSchema,
  inHostMode: hostNonsoleParticipationInHostModeSchema,
  reasons: z.array(participationReasonRowSchema),
});
export type HostNonsoleParticipationSummary = z.infer<typeof hostNonsoleParticipationSummarySchema>;

/**
 * Snapshot totals accept legacy (reachable/dormant) or current
 * (connectedNow/disconnected) connectivity pairs.  At least one complete
 * pair must be present and partial pairs are rejected.
 */
const snapshotTotalsBaseSchema = z.object({
  allActive: z.number().int().nonnegative(),
  uninstalled: z.number().int().nonnegative(),
  connectedNow: z.number().int().nonnegative().optional(),
  disconnected: z.number().int().nonnegative().optional(),
  reachable: z.number().int().nonnegative().optional(),
  dormant: z.number().int().nonnegative().optional(),
});

function validateConnectivityPairs(
  d: z.infer<typeof snapshotTotalsBaseSchema>,
  ctx: z.RefinementCtx,
) {
  const hasConnected = d.connectedNow != null && d.disconnected != null;
  const hasReachable = d.reachable != null && d.dormant != null;
  const partialConnected = (d.connectedNow != null) !== (d.disconnected != null);
  const partialReachable = (d.reachable != null) !== (d.dormant != null);

  if (partialConnected) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'connectedNow and disconnected must both be present or both absent',
    });
  }
  if (partialReachable) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'reachable and dormant must both be present or both absent',
    });
  }
  if (!hasConnected && !hasReachable) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'At least one complete connectivity pair is required (connectedNow/disconnected or reachable/dormant)',
    });
  }
}

export const hostNonsoleParticipationSnapshotTotalsSchema =
  snapshotTotalsBaseSchema.superRefine(validateConnectivityPairs);
export type HostNonsoleParticipationSnapshotTotals = z.infer<
  typeof hostNonsoleParticipationSnapshotTotalsSchema
>;

export const hostNonsoleParticipationSnapshotSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  reachabilityDays: z.number().int().positive(),
  staleMinutes: z.number().int().positive(),
  totals: hostNonsoleParticipationSnapshotTotalsSchema,
  inHostMode: hostNonsoleParticipationInHostModeSchema,
  reasons: z.array(participationReasonRowSchema),
  gates: z.record(z.string(), z.number().int().nonnegative()).optional(),
});
export type HostNonsoleParticipationSnapshot = z.infer<typeof hostNonsoleParticipationSnapshotSchema>;

export const hostNonsoleParticipationTrendResponseSchema = z.object({
  snapshots: z.array(hostNonsoleParticipationSnapshotSchema),
});
export type HostNonsoleParticipationTrendResponse = z.infer<
  typeof hostNonsoleParticipationTrendResponseSchema
>;

// ============================================================================
// Daily accumulator document (MongoDB persistence shape)
// ============================================================================

export const hostNonsoleParticipationDailyDocumentSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  reachabilityDays: z.number().int().positive(),
  staleMinutes: z.number().int().positive(),
  sampleCount: z.number().int().positive(),
  sums: snapshotTotalsBaseSchema.extend({
    hosting: z.number().int().nonnegative(),
    idle: z.number().int().nonnegative(),
    reasons: z.record(z.string(), z.number().int().nonnegative()),
    gates: z.record(z.string(), z.number().int().nonnegative()).optional(),
  }).superRefine(validateConnectivityPairs),
});
export type HostNonsoleParticipationDailyDocument = z.infer<
  typeof hostNonsoleParticipationDailyDocumentSchema
>;

// ============================================================================
// Drill-down machine row
// ============================================================================

export const hostNonsoleParticipationMachineRowSchema = z.object({
  machineId: z.string(),
  hostname: z.string().nullable().optional(),
  lastKnownState: z.string().nullable().optional(),
  nodeServiceLastSeenAt: z.string().nullable().optional(),
  evaluatedAtUtc: z.string().nullable().optional(),
  bucket: participationBucketSchema,
  blockingGateIds: z.array(z.string()),
  blockingReasons: z.array(z.object({
    gateId: z.string(),
    reasonCode: z.string(),
  })),
  presenceProcesses: z.array(z.string()).optional(),
});
export type HostNonsoleParticipationMachineRow = z.infer<
  typeof hostNonsoleParticipationMachineRowSchema
>;

// ============================================================================
// Cohort query / result
// ============================================================================

export const cohortClauseSchema = z.object({
  gateId: z.string(),
  reasonCode: z.string().optional(),
});
export type CohortClause = z.infer<typeof cohortClauseSchema>;

export const cohortGroupSchema = z.object({
  op: z.enum(['OR']),
  clauses: z.array(cohortClauseSchema).min(1).max(8),
});
export type CohortGroup = z.infer<typeof cohortGroupSchema>;

export const hostNonsoleParticipationCohortQuerySchema = z.object({
  groups: z.array(cohortGroupSchema).min(1).max(4),
  groupOp: z.enum(['AND']),
  includeMachines: z.boolean().optional(),
  limit: z.number().int().min(1).max(200).optional(),
  cursor: z.string().optional(),
});
export type HostNonsoleParticipationCohortQuery = z.infer<
  typeof hostNonsoleParticipationCohortQuerySchema
>;

export const hostNonsoleParticipationCohortResultSchema = z.object({
  machineCount: z.number().int().nonnegative(),
  machines: z.array(hostNonsoleParticipationMachineRowSchema).optional(),
  nextCursor: z.string().optional(),
  generatedAt: z.number(),
});
export type HostNonsoleParticipationCohortResult = z.infer<
  typeof hostNonsoleParticipationCohortResultSchema
>;

// ============================================================================
// Reason breakdown (per-gate + per-reasonCode)
// ============================================================================

export const gateReasonBreakdownRowSchema = z.object({
  gateId: z.string(),
  count: z.number().int().nonnegative(),
  reasonCodes: z.array(z.object({
    reasonCode: z.string(),
    count: z.number().int().nonnegative(),
  })),
});
export type GateReasonBreakdownRow = z.infer<typeof gateReasonBreakdownRowSchema>;

export const hostNonsoleParticipationReasonBreakdownSchema = z.object({
  generatedAt: z.number(),
  gates: z.array(gateReasonBreakdownRowSchema),
});
export type HostNonsoleParticipationReasonBreakdown = z.infer<
  typeof hostNonsoleParticipationReasonBreakdownSchema
>;

// ============================================================================
// Top offending processes (presence-hold aggregation)
// ============================================================================

export const hostNonsoleParticipationTopProcessesSchema = z.object({
  generatedAt: z.number(),
  processes: z.array(z.object({
    processName: z.string(),
    count: z.number().int().nonnegative(),
  })).max(50),
});
export type HostNonsoleParticipationTopProcesses = z.infer<
  typeof hostNonsoleParticipationTopProcessesSchema
>;

// ============================================================================
// Fleet extra filter params (v3)
// ============================================================================

export const fleetHeartbeatFilterSchema = z.enum(['connected', 'disconnected']);
export type FleetHeartbeatFilter = z.infer<typeof fleetHeartbeatFilterSchema>;

export const fleetLifecycleFilterSchema = z.enum(['active', 'uninstalled', 'all']);
export type FleetLifecycleFilter = z.infer<typeof fleetLifecycleFilterSchema>;

export const fleetExtraFilterParamsSchema = z.object({
  heartbeat: fleetHeartbeatFilterSchema.optional(),
  hostMode: z.boolean().optional(),
  presenceProcess: z.string().min(1).max(260).optional(),
  policyProcess: z.string().min(1).max(260).optional(),
  lifecycle: fleetLifecycleFilterSchema.optional(),
});
export type FleetExtraFilterParams = z.infer<typeof fleetExtraFilterParamsSchema>;

/**
 * Wire-format parser for query-string values (Express `req.query`).
 * Coerces string query-param values into typed fields:
 * - `heartbeat` / `lifecycle`: accept the enum string as-is.
 * - `hostMode`: accepts `'true'`/`'false'` (strings) or boolean.
 * - `presenceProcess` / `policyProcess`: accepts any non-empty string (max 260 chars).
 * Throws with a Zod-derived message on invalid input.
 */
export function parseFleetExtraFilterParams(
  query: Record<string, unknown>,
): FleetExtraFilterParams {
  const raw: Record<string, unknown> = {};
  if (query['heartbeat'] !== undefined) raw['heartbeat'] = query['heartbeat'];
  if (query['lifecycle'] !== undefined) raw['lifecycle'] = query['lifecycle'];
  if (query['presenceProcess'] !== undefined) {
    const v = query['presenceProcess'];
    if (typeof v === 'string' && v.length > 0 && v.trim().length === 0) {
      // whitespace-only → silently ignore (consistent with parseStringFilter)
    } else {
      raw['presenceProcess'] = typeof v === 'string' ? v.trim() : v;
    }
  }
  if (query['policyProcess'] !== undefined) {
    const v = query['policyProcess'];
    if (typeof v === 'string' && v.length > 0 && v.trim().length === 0) {
      // whitespace-only → silently ignore (consistent with parseStringFilter)
    } else {
      raw['policyProcess'] = typeof v === 'string' ? v.trim() : v;
    }
  }
  if (query['hostMode'] !== undefined) {
    const v = query['hostMode'];
    if (v === 'true' || v === true) raw['hostMode'] = true;
    else if (v === 'false' || v === false) raw['hostMode'] = false;
    else raw['hostMode'] = v;
  }
  const result = fleetExtraFilterParamsSchema.parse(raw);
  return result;
}

// ============================================================================
// Helpers
// ============================================================================

export function buildEmptyReasonCounts(): Record<ParticipationBucket, number> {
  return Object.fromEntries(
    participationBucketValues.map((b) => [b, 0]),
  ) as Record<ParticipationBucket, number>;
}

export function buildReasonRows(
  counts: Record<ParticipationBucket, number>,
  includeBuckets: ParticipationBucket[] = NON_PARTICIPATION_BUCKETS,
): ParticipationReasonRow[] {
  return includeBuckets
    .filter((bucket) => counts[bucket] > 0)
    .map((bucket) => ({
      bucket,
      label: PARTICIPATION_BUCKET_LABELS[bucket],
      count: counts[bucket],
    }));
}
